import { Injectable } from '@angular/core';
import {Observable, of} from 'rxjs';
import {distinctUntilChanged, filter, map, shareReplay, switchMap} from 'rxjs/operators';
import {ActivatedRoute} from '@angular/router';

import {CreBankAccountDataService} from '../../../../services/gp/cre-bank-account-data.service';
import {UnitPaymentsDataService} from '../../../../services/shared/unit-payments-data.service';
import {PaymentReadDto} from './models/payment/payment-read-dto.model';
import {BasePhoneVerificationStrategy} from '../../../models/phone-verification-strategies/base-phone-verification-strategy.model';
import {PhoneVerificationReqRes} from '../../phone-verification-dialog/PhoneVerificationReqRes.model';
import {PhoneVerificationReq} from '../../phone-verification-dialog/PhoneVerificationReq.model';
import {TerraUtils} from '../../../TerraUtils';
import { UnitBankAccountSearchOptions } from 'src/app/shared/models/unit-bank-account/UnitBankAccountSearchOptions.model';
import { UnitApplicationParentType } from 'src/app/shared/enums/unit-bank-account/UnitApplicationParentType.enum';
import { PaymentCreateDto } from './models/payment/payment-create-dto.model';
import { UnitBankAccountDataService } from 'src/app/services/shared/unit-bank-account-data.service';
import { UserService } from 'src/app/services/shared/user.service';
import { AppSettingsService, SubdomainEnum } from 'src/app/services/app-settings.service';
import { Title } from '@angular/platform-browser';

@Injectable()
export class UnitPaymentService extends BasePhoneVerificationStrategy {
  readonly forbiddenNameCharacters = TerraUtils.consts.validators.FORBIDDEN_CHARACTERS_IN_NAME;
  
  isGp = this.appSettingService.subDomain === SubdomainEnum.GP;
  
  private bankAccountsSearchOptions = {
    includeLimits: true,
    filter: '',
    pageNumber: 0,
    pageSize: 1000,
    sortOrder: 'asc',
    orderBy: 'AccountNickname',    
    parentType: this.isGp ? UnitApplicationParentType.Holding | UnitApplicationParentType.Organization : UnitApplicationParentType.LP 
  } as UnitBankAccountSearchOptions;

  // isGp$ = of(this.appSettingService.subDomain === SubdomainEnum.GP);


  /**
   * Pre selected payment type (ACH/Wire), taken from query string
   */
  preSelectedPaymentType$ = this.route.queryParams.pipe(
    map(queryParams => queryParams.type ? +queryParams.type : null)
  );

  /**
   * Pre selected source bank account id
   */
  preSelectedBankAccountId$ = this.route.queryParams.pipe(
    map(queryParams => queryParams.account ? +queryParams.account : null)
  );

  /**
   * Payment id of a payment to repeat
   */
  paymentIdToRepeat$ = this.route.queryParams.pipe(
    map(queryParams => queryParams.repeat ? +queryParams.repeat : null)
  );

  /**
   * The relevant payment id, taken from URL
   */
  paymentId$ = this.route.params.pipe(
    distinctUntilChanged(),
    map(params => params.paymentId ? +params.paymentId : null)
  );

  /**
   * Payment details
   */
  payment$: Observable<PaymentReadDto> = this.paymentId$.pipe(
    switchMap((paymentId) => {
      if (paymentId === null || paymentId === undefined || isNaN(paymentId)) {
        return of(null);
      }

      return this.crePaymentDataService.getById(paymentId);
    }),
    shareReplay(1)
  );

  /**
   * A list of CRE bank accounts which belongs to this organization
   */
  bankAccounts$ = this.unitBankAccountDataService.getUnitBankAccounts(this.bankAccountsSearchOptions).pipe(
    map(accounts => accounts.rows),
    shareReplay(1)
  );

  /**
   * Selected source CRE bank account selected for the payment
   */
  sourceBankAccount$ = this.payment$.pipe(
    filter(payment => !!payment),
    switchMap(payment => this.unitBankAccountDataService.getById(payment.sourceUnitBankAccountId)),
    shareReplay(1)
  );

  constructor(private unitBankAccountDataService: UnitBankAccountDataService,
              private crePaymentDataService: UnitPaymentsDataService,
              private appSettingService: AppSettingsService,
              private titleService: Title,
              private route: ActivatedRoute) {
    super();
  }


  setPageTitle(title:string){
    if(this.isGp){
      this.titleService.setTitle(`${title} ${TerraUtils.consts.GpPageTitleSuffix}`)
    }
    else{
      this.titleService.setTitle(`${title} ${TerraUtils.consts.LpPageTitleSuffix}`)
    }
  }

  /**
   * Sends a request to create payment
   * @param paymentRequest Payment detgails
   */
  public sendPayment(paymentRequest: PaymentCreateDto): Observable<PaymentReadDto> {
    return this.crePaymentDataService.initPayment(paymentRequest);
  }

  /**
   * Sends a request to confirm and execute the payment
   */
  public confirmPayment() {
    return this.paymentId$.pipe(
      switchMap(paymentId => this.crePaymentDataService.confirmPayment(paymentId))
    );
  }

  /**
   * Updates an existing payment
   * @param paymentId Payment id
   * @param model Payment details
   */
  public updatePayment(paymentId: number, model: PaymentCreateDto) {
    return this.crePaymentDataService.updatePaymentDetails(paymentId, model);
  }

  /**
   * Gets payments details by id
   * @param paymentId Payment id
   */
  public getPaymentById(paymentId: number) {
    return this.crePaymentDataService.getById(paymentId);
  }

  // Phone verification interface

  public resendVerificationCode(): Observable<PhoneVerificationReqRes> {
    return this.sendVerificationCode();
  }

  public sendVerificationCode(): Observable<PhoneVerificationReqRes> {
    return this.paymentId$.pipe(
      switchMap(paymentId => this.crePaymentDataService.sendOtp(paymentId))
    );
  }

  public verifyPhone(request: PhoneVerificationReq): Observable<PhoneVerificationReqRes> {
    return this.paymentId$.pipe(
      switchMap(paymentId => this.crePaymentDataService.verifyPhone(paymentId, request))
    );
  }
}
